import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Breadcrumb, BreadcrumbItem, Container, Form, Row, Spinner } from "reactstrap";
import { useSetRecoilState } from "recoil";
import { CustomLink } from "src/components/Common/CustomLink";
import FormInput from "src/components/Common/Form/FormInput";
import DatePickerCustomField from "src/components/Common/Form/DatePickerCustomField";
import { navigationPromptState } from "src/recoil/atoms/global";
import APP_ROUTES from "src/routes/routes";
import { FORM_NAME } from "../new";
import { useTranslation } from "react-i18next";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";
import FormSelect from "src/components/Common/Form/FormSelect";
import useGetProjectInstanceList from "src/components/ProjectInstance/useGetProjectInstanceList";
import { PATTERN_ALPHANUMERIC_ID } from "src/constant/common";

const CreateTestPackageForm = ({ submitData, data, title, getRedirectURL = () => APP_ROUTES.TEST_PACKAGE }) => {
  const { t } = useTranslation("test-package");
  const navigate = usePatchedNavigate();
  const navigationPrompt = useSetRecoilState(navigationPromptState);
  const [isSubmit, setIsSubmit] = useState(false);
  const { projectInstanceList: projectInstances } = useGetProjectInstanceList({
    isSelectOptions: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    watch,
  } = useForm({
    defaultValues: data,
  });

  const handleSubmitForm = (data) => {
    navigationPrompt(false);
    setIsSubmit(true);
    const formData = {
      ...data,
      [FORM_NAME.ID]: data[FORM_NAME.ID]?.trim(),
      [FORM_NAME.TITLE]: data[FORM_NAME.TITLE]?.trim(),
    };
    const cleanedData = Object.fromEntries(Object.entries(formData).filter(([_, value]) => value !== ""));
    return submitData({ data: cleanedData }, (isSubmitFailed) => {
      setIsSubmit(false);
      isSubmitFailed && isDirty && navigationPrompt(true);
    });
  };

  // const handleUpdateAttachmentsFile = (files, fieldName, isSingleFile) => {
  //   if (isSingleFile) {
  //     setValue(fieldName, files[0]);
  //     return;
  //   }
  //   setValue(fieldName, files);
  //   if (watch(fieldName)) {
  //     clearErrors(fieldName);
  //   }
  // };

  useEffect(() => {
    if (isDirty) {
      navigationPrompt(true);
    }
  }, [isDirty]);

  return (
    <Container fluid className="test-package-container">
      <Breadcrumb>
        <BreadcrumbItem>
          <CustomLink to={getRedirectURL()} className="link-primary">
            {t("list.title")}
          </CustomLink>
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("new.title_breadcrumb")}</BreadcrumbItem>
      </Breadcrumb>

      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="header-title">{title || t("new.title")}</div>
              <Form onSubmit={handleSubmit(handleSubmitForm)} className="simulator-6dof-form">
                <div className="row">
                  <div className="col-md-6">
                    <div className="pe-md-5">
                      <FormInput
                        register={register}
                        label={t("new.id")}
                        classGroup={errors?.[FORM_NAME.ID]?.message && "m-b-4"}
                        name={FORM_NAME.ID}
                        placeholder={t("new.id_placeholder")}
                        errors={errors?.[FORM_NAME.ID]?.message}
                        requiredMessage={t("new.id_required")}
                        required
                        maxLength={120}
                        maxLengthMessage={t("maxlength", { max_length: 120 })}
                        pattern={{
                          value: PATTERN_ALPHANUMERIC_ID,
                          message: t(`pattern_alphanumeric_id`, { field_name: t("new.id"), max_length: 120 }),
                        }}
                        errorsRelative
                      />
                      <FormInput
                        register={register}
                        label={t("new.name")}
                        name={FORM_NAME.TITLE}
                        placeholder={t("new.name_placeholder")}
                        errors={errors?.[FORM_NAME.TITLE]?.message}
                        requiredMessage={t("new.name_required")}
                        required
                        maxLength={255}
                        maxLengthMessage={t("maxlength", { max_length: 255 })}
                      />
                      <FormSelect
                        label={t("new.project_instance")}
                        name={FORM_NAME.YAMCS_PACKAGE_ID}
                        placeholder={t("new.project_instance_placeholder")}
                        control={control}
                        errors={errors?.[FORM_NAME.YAMCS_PACKAGE_ID]?.message}
                        required
                        isSearchable
                        options={projectInstances}
                        watch={watch(FORM_NAME.YAMCS_PACKAGE_ID)}
                        requiredMessage={t("new.project_instance_required")}
                      />
                      <DatePickerCustomField
                        control={control}
                        label={t(`new.plan_start_date`)}
                        name={FORM_NAME.PLAN_START_DATE}
                        placeholder={t(`new.plan_start_date_placeholder`)}
                        errors={errors?.[FORM_NAME.PLAN_START_DATE]?.message}
                        minDate={new Date()}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <button
                    disabled={isSubmit}
                    className="btn btn-secondary width-by-px-200 height-by-px-38"
                    onClick={() => navigate(getRedirectURL())}
                    type="button"
                  >
                    <span>{t("cancel")}</span>
                  </button>
                  <button className="btn btn-primary width-by-px-200 height-by-px-38" disabled={isSubmit} type="submit">
                    {isSubmit ? <Spinner size="sm" /> : <span>{t("submit")}</span>}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default CreateTestPackageForm;
